/* begin general import */
import Close16 from "@carbon/icons-react/es/close/16";
import Send16 from "@carbon/icons-react/es/send/16";
import { DownOutlined } from "@ant-design/icons";
import { Time16 } from "@carbon/icons-react";
import { Col, Empty, Row, Spin, Switch, Tree } from "antd";
import { Model, ModelFilter } from "react3l-common";
import { API_DOCUMENT_PERMISSION_PREFIX } from "config/api-consts";
import { DOCUMENT_PERMISSION_ROUTE } from "config/route-consts";
import { Permission } from "models/Permission";
import { Action } from "models/Action";
import { Observable } from "rxjs";
import React from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  FormItem,
  InputSearch,
  InputText,
  Select,
} from "react3l-ui-library";
import { permissionRepository } from "repositories/permission-repository";
import appMessageService from "services/common-services/app-message-service";
import { authorizationService } from "services/common-services/authorization-service";
import { utilService } from "services/common-services/util-service";
import {
  ModelActionEnum,
  detailService,
} from "services/page-services/detail-service";
import { fieldService } from "services/page-services/field-service";
import { masterService } from "services/page-services/master-service";
import { queryStringService } from "services/page-services/query-string-service";
import nameof from "ts-nameof.macro";
import "./PermissionDetail.scss";
/* end general import */

function PermissionDetail() {
  const [translate] = useTranslation();

  const { validAction } = authorizationService.useAuthorizedAction(
    DOCUMENT_PERMISSION_ROUTE.split("/")[DOCUMENT_PERMISSION_ROUTE.split("/").length - 1],
    API_DOCUMENT_PERMISSION_PREFIX
  );

  const { handleGoHistory } = masterService.useMasterAction(DOCUMENT_PERMISSION_ROUTE);

  const { model, dispatch } = detailService.useModel<Permission>(Permission);

  const { isDetail } = detailService.useGetIsDetail<Permission>(
    permissionRepository.get,
    dispatch
  );

  /* Save button visibility:
     - create screen  -> needs the "create" permission
     - edit screen    -> needs the "update" permission */
  const canSave = React.useMemo(
    () => (isDetail ? validAction("update") : validAction("create")),
    [isDetail, validAction]
  );

  /* view-only mode when navigated with ?view=true */
  const { view } = queryStringService.useGetQueryString("view");
  const isView = React.useMemo(() => view === "true", [view]);

  const {
    handleChangeAllField,
    handleChangeSingleField,
  } = fieldService.useField(model, dispatch);

  const { handleGoMaster } = detailService.useActionsDetail<Permission>(
    model,
    permissionRepository.save,
    handleChangeAllField,
    DOCUMENT_PERMISSION_ROUTE
  );

  const { notifyUpdateItemSuccess } = appMessageService.useCRUDMessage();

  const [isLoading, setIsLoading] = React.useState(false);

  /* Classification dropdown: 2 fixed FE options (same as master).
     "Thư mục" -> "Document", "Tài liệu" -> "File". */
  const typeOptions = React.useMemo(
    () => [
      { id: 1, name: translate("permissions.folder"), code: "Document" },
      { id: 2, name: translate("permissions.file"), code: "File" },
    ],
    [translate]
  );

  const getTypeList = React.useCallback(
    () =>
      new Observable<Model[]>((observer) => {
        observer.next(typeOptions as Model[]);
        observer.complete();
      }),
    [typeOptions]
  );

  const selectedType = React.useMemo(
    () => typeOptions.find((o) => o.code === model.typeCode),
    [typeOptions, model.typeCode]
  );

  /* ── action list state ── */
  const [actionList, setActionList] = React.useState<Action[]>([]);
  const [actionSearch, setActionSearch] = React.useState<string>("");
  const [loadingActions, setLoadingActions] = React.useState(false);

  /* load actions for the current classification + record (id) so isSelected is correct */
  const loadDocumentActions = React.useCallback(
    (typeCode?: string, id?: number) => {
      if (!typeCode) {
        setActionList([]);
        return;
      }
      setLoadingActions(true);
      permissionRepository
        .listDocumentActions({ typeCode, id } as any)
        .subscribe(
          (actions: Action[]) => {
            setActionList(actions);
            setLoadingActions(false);
          },
          () => setLoadingActions(false)
        );
    },
    []
  );

  /* fetch actions whenever classification (typeCode) or record (id) changes */
  React.useEffect(() => {
    loadDocumentActions(model.typeCode, model.id);
  }, [model.typeCode, model.id, loadDocumentActions]);

  const filteredActions = React.useMemo(() => {
    if (!actionSearch.trim()) return actionList;
    return actionList.filter((a) =>
      a.name?.toLowerCase().includes(actionSearch.toLowerCase())
    );
  }, [actionList, actionSearch]);

  /* flat tree: each action is a checkable leaf node.
     In view mode disable only the checkbox (disableCheckbox) so the title text stays bright. */
  const treeData = React.useMemo(
    () =>
      filteredActions.map((action) => ({
        title: action.name,
        key: String(action.id),
        disableCheckbox: isView,
      })),
    [filteredActions, isView]
  );

  /* checkbox state is driven by each action's `isSelected` flag */
  const checkedKeys = React.useMemo(
    () => filteredActions.filter((a) => a.isSelected).map((a) => String(a.id)),
    [filteredActions]
  );

  const onCheckTree = React.useCallback(
    (checked: any) => {
      const checkedArr: string[] = Array.isArray(checked)
        ? checked
        : checked.checked;
      const checkedIds = new Set(checkedArr.map((k) => Number(k)));
      const filteredIds = new Set(filteredActions.map((a) => a.id));
      setActionList((prev) =>
        prev.map((a) =>
          filteredIds.has(a.id)
            ? { ...a, isSelected: checkedIds.has(a.id) }
            : a
        )
      );
    },
    [filteredActions]
  );

  /* status toggle */
  const handleChangeStatus = React.useCallback(
    (checked: boolean) => {
      const newModel = { ...model };
      newModel.statusId = checked ? 1 : 0;
      handleChangeAllField(newModel);
    },
    [handleChangeAllField, model]
  );

  /* classification select — clear action mappings when classification changes */
  const handleChangeType = React.useCallback(
    (_id: number, item: any) => {
      dispatch({
        type: ModelActionEnum.UPDATE,
        payload: {
          typeCode: item?.code,
          documentPermissionFieldMappings: [],
        },
      });
    },
    [dispatch]
  );

  const handleSavePermission = React.useCallback(() => {
    setIsLoading(true);
    const isUpdate = !!model.id;
    const payload = Object.assign(new Permission(), {
      id: model.id ?? 0,
      code: model.code,
      name: model.name,
      statusId: model.statusId,
      typeCode: model.typeCode,
      documentPermissionFieldMappings: actionList
        .filter((a) => a.isSelected)
        .map((a) => ({ actionId: a.id })),
    });
    permissionRepository.save(payload).subscribe(
      (res) => {
        notifyUpdateItemSuccess();
        setIsLoading(false);
        if (isUpdate) {
          /* stay on detail, reload the record + list-document-actions */
          handleChangeAllField(res);
          loadDocumentActions(res.typeCode ?? model.typeCode, res.id ?? model.id);
        } else {
          handleGoMaster();
        }
      },
      (error) => {
        if (error.response && error.response.status === 400) {
          /* field-level errors populate inline; the global http interceptor
             already shows the "kiểm tra lại các trường dữ liệu" message,
             so we don't fire an extra "Cập nhật có lỗi" notification here. */
          handleChangeAllField(error.response?.data);
        }
        setIsLoading(false);
      }
    );
  }, [
    actionList,
    handleChangeAllField,
    handleGoMaster,
    loadDocumentActions,
    model,
    notifyUpdateItemSuccess,
  ]);

  return (
    <Spin spinning={isLoading}>
      <div className="page-content page-content-dxg" id="page-content-dxg-id">
        <div className="info-component ml-4 mb-3">
          <span>{translate("general.defaultHeader.account")}</span>
          <span> &gt; </span>
          <span className="info-primary">
            {translate("menu.title.folderPermissionGroup")}
          </span>
        </div>

        <div className="page page-detail p-t--lg p-l--xxl p-r--xxl p-b--lg">
          <div className="mb-0 d-flex justify-content-between background-w-dxg border-tl-8-dxg">
            <h5 className="font-bold page-title">
              {translate("menu.title.folderPermissionGroup")}
            </h5>
            <div className="mr-3">
              <Button
                type="secondary"
                className="btn--lg btn-red"
                icon={<Close16 />}
                onClick={handleGoMaster}
              >
                {translate("general.actions.close")}
              </Button>

              <Button
                type="secondary"
                className="btn--lg btn-orange-export ml-3"
                icon={<Time16 />}
                onClick={handleGoHistory(model?.id)}
              >
                {translate("general.actions.viewHistory")}
              </Button>

              {!isView && canSave && (
                <Button
                  type="primary"
                  className="btn--lg btn-orange-wIcon ml-3"
                  icon={<Send16 />}
                  onClick={handleSavePermission}
                >
                  {translate("general.actions.save")}
                </Button>
              )}
            </div>
          </div>

          <div className="page-detail__title p-b--sm">
            {!isDetail
              ? translate("general.actions.create")
              : translate("general.detail.title")}
          </div>

          <Row>
            {/* ── Left: form fields ── */}
            <Col lg={8}>
              <Col lg={24} className="m-b--xxs p-r--sm">
                <FormItem>
                  <InputText
                    label={translate("permissions.systemCode")}
                    type={0}
                    value={model.id?.toString()}
                    placeHolder={translate("permissions.placeholder.systemCode")}
                    className={"tio-account_square_outlined"}
                    disabled={true}
                  />
                </FormItem>
              </Col>

              <Col lg={24} className="m-b--xxs p-r--sm">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    model,
                    nameof(model.code)
                  )}
                  message={model.errors?.code}
                >
                  <InputText
                    isRequired
                    maxLength={200}
                    label={translate("permissions.code")}
                    type={0}
                    value={model.code}
                    placeHolder={translate("permissions.placeholder.code")}
                    className={"tio-account_square_outlined"}
                    disabled={isDetail}
                    onChange={handleChangeSingleField({
                      fieldName: nameof(model.code),
                    })}
                  />
                </FormItem>
              </Col>

              <Col lg={24} className="m-b--xxs p-r--sm">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    model,
                    nameof(model.name)
                  )}
                  message={model.errors?.name}
                >
                  <InputText
                    isRequired
                    maxLength={500}
                    label={translate("permissions.name")}
                    type={0}
                    value={model.name}
                    placeHolder={translate("permissions.placeholder.name")}
                    className={"tio-account_square_outlined"}
                    disabled={isView}
                    onChange={handleChangeSingleField({
                      fieldName: nameof(model.name),
                    })}
                  />
                </FormItem>
              </Col>

              <Col lg={24} className="m-b--xxs p-r--sm">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    model,
                    nameof(model.typeCode)
                  )}
                  message={model.errors?.typeCode}
                >
                  <Select
                    isRequired
                    label={translate("permissions.classification")}
                    type={0}
                    classFilter={ModelFilter}
                    searchProperty={"name"}
                    placeHolder={translate("permissions.placeholder.classification")}
                    getList={getTypeList}
                    onChange={handleChangeType}
                    value={selectedType}
                    disabled={isDetail}
                  />
                </FormItem>
              </Col>

              <Col lg={24} className="m-b--xxs p-r--sm">
                <FormItem
                  validateStatus={utilService.getValidateStatus(
                    model,
                    nameof(model.status)
                  )}
                  message={model.errors?.status}
                >
                  <div>
                    <div className="label-status">
                      {translate("permissions.status")}
                    </div>
                    <Switch
                      checked={model.statusId === 1 ? true : false}
                      onChange={handleChangeStatus}
                      className="switch_status"
                      disabled={isView}
                    />
                  </div>
                </FormItem>
              </Col>
            </Col>

            <Col lg={1}></Col>

            {/* ── Right: action checklist (copied from role-detail) ── */}
            <Col lg={13} className="permission-tree-view">
              <h5 className="page-detail__title p-b--sm p-t--sm p-l--sm">
                {translate("permissions.actionList")}
              </h5>
              <div style={{ height: "500px" }}>
                <Row>
                  <Col lg={1}></Col>
                  <Col lg={13}>
                    <InputSearch
                      value={actionSearch}
                      classFilter={ModelFilter}
                      placeHolder={translate("general.placeholder.search")}
                      onChange={(value: string) => setActionSearch(value)}
                      animationInput={false}
                    />
                  </Col>

                  <Col lg={24} className="m-t--lg m-l--lg">
                    {loadingActions ? (
                      <Spin className="d-flex justify-content-center align-items-center" />
                    ) : treeData.length > 0 ? (
                      <div
                        style={{
                          height: "400px",
                          width: "90%",
                          overflowY: "auto",
                          overflowX: "hidden",
                        }}
                      >
                        <Tree
                          checkable
                          onCheck={onCheckTree}
                          treeData={treeData}
                          showLine={{ showLeafIcon: false }}
                          switcherIcon={<DownOutlined />}
                          checkedKeys={checkedKeys}
                        />
                      </div>
                    ) : (
                      <Empty
                        description={
                          model.typeCode
                            ? translate("permissions.noActions")
                            : translate("permissions.selectClassificationFirst")
                        }
                      />
                    )}
                  </Col>
                </Row>
              </div>
            </Col>
          </Row>
        </div>
      </div>
    </Spin>
  );
}

export default PermissionDetail;
